import AppFadeView from "@toothfairy/shared-ui/AppFadeView";
import AppForms from "@toothfairy/shared-ui/AppForms";
import AppMenu from "@toothfairy/shared-ui/AppMenu";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, { forwardRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import sitesHelper from "../../../../API/Sites/sitesHelper";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { isLargeScreen } from "../../../MainViewContainer";
import BenchmarkSettingsView from "../SubComponents/BenchmarkSettingsView";
import benchmarksHelper from "../../../../API/Benchmarks/benchmarksHelper";
import { createBenchmark } from "../../../../graphql/mutations";

const BenchmarkSettings = forwardRef(
  (
    {
      sectionTitle,
      initialMapValues,
      settingType,
      isEdit,
      opt,
      setDisplayedView,
      backTo,
      onFunctionCreated,
      selectedId,
      setShowSiteModal,
      displayTokenSection,
      setDisplayTokenSection,
      setInitialMapValues,
      setShowBenchmarkModal,
      onBenchmarkDeleted = () => {},
      isBuilderLayout,
      builderActiveSection,
      onBuilderContextChange,
      saveVersion,
    },
    ref
  ) => {
    const AppToasty = AppToast.useToast();
    const { t } = useTranslation();
    const { state, dispatch } = AppStateManager.useAppStateManager();
    const { getActiveWorkspace, getPlanConfig } = useWorkspaces();

    const validationSchema = AppForms.yup.object().shape({
      // allow for no more than 4 words in sourceLanguage and targetLanguage
      name: AppForms.yup.string().required().label().max(255),
      description: AppForms.yup.string().nullable().label().max(2048),
      questions: AppForms.yup.array().of(
        AppForms.yup.object().shape({
          question: AppForms.yup.string().nullable().label().max(255),
          answer: AppForms.yup.string().nullable().label().max(255),
        })
      ),
    });

    const handleDeletion = async (id) => {
      try {
        const result = await benchmarksHelper.deleteBenchmark(id);
        dispatch("SET_BENCHMARKS", [
          ...state.benchmarks?.filter((u) => u?.id !== id),
        ]);
        AppToasty.show(t("benchmarks.deletionSuccess"), {
          type: "success",
        });

        setShowSiteModal?.(false);
        setShowBenchmarkModal?.(false);
        onBenchmarkDeleted();
      } catch (error) {
        console.error("benchmarkDeleteErrorCaption", error);
        AppToasty.show(t("benchmarks.deletionFailed"), {
          type: "danger",
        });
      }
    };
    const identifyOptByID = (id) => {
      return state.benchmarks.find((u) => u?.id === id);
    };

    const handleBenchmarkItemSave = async (submitted) => {
      let v = { ...submitted };
      if (!v?.benchmarks || v.benchmarks?.length === 0)
        return AppToasty.show(t("benchmarks.minQuestionsRequired"), {
          type: "danger",
        });
      console.log("values", v);

      let newBenchmarks =
        state.benchmarks?.length > 0 ? [...state.benchmarks] : [];
      try {
        let result;
        if (isEdit) {
          if (v?.benchmarks) delete v.benchmarks;

          result = await benchmarksHelper.updateBenchmark(selectedId, [
            {
              property: "name",
              value: v.name,
            },
            {
              property: "description",
              value: v.description,
            },
            {
              property: "questions",
              value: v.questions || v.benchmarks,
            },
            {
              property: "files",
              value: v.files,
            },
          ]);
          result = result?.data?.updateBenchmark;
          const _updatedObject = identifyOptByID(result?.id);
          const updateIndex = newBenchmarks.findIndex(
            (u) => u?.id === _updatedObject?.id
          );
          newBenchmarks[updateIndex] = result;
        } else {
          if (getPlanConfig()?.maxBenchmarks <= newBenchmarks.length)
            return AppToasty.show(
              t("benchmarks.maxBenchmarksReached", {
                maxBenchmarks: getPlanConfig()?.maxBenchmarks,
              }),
              {
                type: "danger",
              }
            );
          v["questions"] = v?.benchmarks || [];
          delete v.benchmarks;
          result = await benchmarksHelper.createBenchmark({
            ...v,
            workspaceID: getActiveWorkspace()?.id,
          });
          result = result?.data?.createBenchmark;
          newBenchmarks.push(result);
        }

        dispatch("SET_BENCHMARKS", [...newBenchmarks]);
        if (!isEdit) {
          setInitialMapValues && setInitialMapValues({ ...result });
        }
        setShowBenchmarkModal?.(false);
        backTo && navigateBack();
        const savedId = result?.id;
        onFunctionCreated && onFunctionCreated("success", savedId, submitted);
      } catch (error) {
        console.error(error);
        onFunctionCreated && onFunctionCreated("error", null, submitted);
      } finally {
        setShowBenchmarkModal?.(false);
      }
    };

    const _view = useMemo(
      () => (
        <AppFadeView
          style={
            isBuilderLayout
              ? {
                  flex: 1,
                  paddingHorizontal: 24,
                  paddingTop: 20,
                  paddingBottom: 40,
                  minWidth: 300,
                }
              : {
                  horizontalMargin: 10,
                  paddingHorizontal: 20,
                  minWidth: isLargeScreen() ? "100%" : 300,
                  paddingBottom: 10,
                  // height: 500,
                }
          }
        >
          {backTo && (
            <AppMenu.DefaultBackOptionComponent
              navigateBack={navigateBack}
              sectionTitle={t("benchmarks.benchmarkSectionTitle", {
                sectionTitle,
              })}
              isClickable
              isBackIconDisplayed
            />
          )}

          <AppForms.AppForm
            key={selectedId}
            ref={ref}
            initialValues={initialMapValues}
            onSubmit={handleBenchmarkItemSave}
            validationSchema={validationSchema}
            enableReinitialize
          >
            <BenchmarkSettingsView
              setDisplayTokenSection={setDisplayTokenSection}
              displayTokenSection={displayTokenSection}
              handleDeletion={handleDeletion}
              isEdit={isEdit}
              settingType={settingType}
              isBuilderLayout={isBuilderLayout}
              builderActiveSection={builderActiveSection}
              onBuilderContextChange={onBuilderContextChange}
              saveVersion={saveVersion}
            />
          </AppForms.AppForm>
        </AppFadeView>
      ),
      [
        state.activeWorkspaceID,
        JSON.stringify(initialMapValues),
        JSON.stringify(state.benchmarks),
        isEdit,
        displayTokenSection,
        AppToasty,
        isBuilderLayout,
        builderActiveSection,
        saveVersion,
      ]
    );
    return _view;
  }
);

export default BenchmarkSettings;
